import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CartItem } from 'types/cart';
import { formatCurrency } from 'utils/formatters';
import { getLazyImageProps, getProductImageUrl } from 'utils/productImage';
import { ROUTES } from 'config/routes';
import CartQuantityStepper from './CartQuantityStepper';

interface CartLineItemProps {
  item: CartItem;
  currency?: string;
  compact?: boolean;
  disabled?: boolean;
  onQuantityChange?: (quantity: number) => void;
  onRemove?: () => void;
  removeLabel?: string;
  decreaseLabel?: string;
  increaseLabel?: string;
  quantityLabel?: string;
}

const productPath = (item: CartItem) => {
  const slug = item.slug?.trim() && item.slug !== 'item' ? item.slug : item.productId;
  return ROUTES.PRODUCT.replace(':slug', encodeURIComponent(slug));
};

const CartLineItem: React.FC<CartLineItemProps> = ({
  item,
  currency = 'ARS',
  compact = false,
  disabled = false,
  onQuantityChange,
  onRemove,
  removeLabel = 'Eliminar',
  decreaseLabel,
  increaseLabel,
  quantityLabel,
}) => {
  const { t } = useTranslation('cart');
  const thumbSize = compact ? 56 : 80;
  const sku = item.productId?.trim();
  const lineTotal = item.unitPrice * item.quantity;
  const imageProps = getLazyImageProps(
    getProductImageUrl(item.imageUrl, item.productId),
    item.name
  );

  return (
    <Box
      sx={{
        display: 'flex',
        gap: compact ? 1.5 : 2,
        alignItems: 'flex-start',
        py: compact ? 1.25 : 2,
      }}
    >
      <Box
        component={RouterLink}
        to={productPath(item)}
        sx={{
          width: thumbSize,
          height: thumbSize,
          flexShrink: 0,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          {...imageProps}
          sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 0.5 }}
        />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component={RouterLink}
          to={productPath(item)}
          variant={compact ? 'body2' : 'body1'}
          fontWeight={600}
          color="text.primary"
          sx={{
            textDecoration: 'none',
            display: '-webkit-box',
            WebkitLineClamp: compact ? 2 : 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            '&:hover': { color: 'primary.main' },
          }}
        >
          {item.name}
        </Typography>

        {sku ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {t('sku', { code: sku })}
          </Typography>
        ) : null}

        {!compact && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {formatCurrency(item.unitPrice, currency)} c/u
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mt: compact ? 1 : 1.5,
            flexWrap: 'wrap',
          }}
        >
          {onQuantityChange ? (
            <CartQuantityStepper
              quantity={item.quantity}
              disabled={disabled}
              size={compact ? 'small' : 'medium'}
              decreaseLabel={decreaseLabel}
              increaseLabel={increaseLabel}
              quantityLabel={quantityLabel ?? t('quantity')}
              onChange={onQuantityChange}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              × {item.quantity}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant={compact ? 'body2' : 'subtitle1'} fontWeight={700}>
              {formatCurrency(lineTotal, currency)}
            </Typography>
            {onRemove && (
              <IconButton
                size="small"
                aria-label={removeLabel}
                disabled={disabled}
                onClick={onRemove}
                sx={{ color: 'text.secondary' }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CartLineItem;
